import React, { useMemo } from 'react'
import { AspectRatio, Image, Table, Text } from '@mantine/core'
import dayjs from 'dayjs'
import classes from './PerformanceComponents.module.css'
import { getRankSuffix } from '../../common/helpers'
import circlePlaceholder from '../../assets/circle-placeholder.svg'
import { FmtColored } from '../../widgets/format'
import { useWidgetContext } from '../../widgets/providers'
import { Typography } from '../../common/typography'

type TableType = 'month' | 'quarter' | 'year'

type Data = {
  id: number
  name: string
  logo: string
  rank: number
  ytd: number
  values: Record<string, number | undefined>
}

interface Props {
  table_type: TableType
  data: Data[]
  rank_offset?: number
}

export default function PerformanceKpiTable({
  table_type,
  data,
  rank_offset = 0,
}: Props) {
  const ctx = useWidgetContext()
  const metric = ctx.metric || ''
  const subject_type = ctx.subject_type || 'team'
  const component = ctx.component || 'percent'
  const statistic = ctx.statistic || 'value'
  const dynamicKeys = Object.keys(data[0]?.values ?? {})

  // Sorting state
  const [sortConfig, setSortConfig] = React.useState<{
    key: string | null
    direction: 'asc' | 'desc'
  }>({ key: 'ytd', direction: 'desc' }) // Default: YTD descending

  const handleSort = (key: string) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    setSortConfig({ key, direction })
  }

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data

    // Missing values should always sort to the bottom, regardless of
    // direction, so treat them as the "worst" value for the current direction.
    const nullValue =
      sortConfig.direction === 'asc'
        ? Number.POSITIVE_INFINITY
        : Number.NEGATIVE_INFINITY

    const sorted = [...data].sort((a, b) => {
      let aValue: number = nullValue
      let bValue: number = nullValue

      if (sortConfig.key === 'ytd') {
        aValue = a.ytd ?? nullValue
        bValue = b.ytd ?? nullValue
      } else if (sortConfig.key?.startsWith('month-')) {
        const month = sortConfig.key.replace('month-', '')
        aValue = a.values[month] ?? nullValue
        bValue = b.values[month] ?? nullValue
      }

      if (sortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return bValue < aValue ? -1 : bValue > aValue ? 1 : 0
      }
    })

    // Reassign ranks based on new sort order
    return sorted.map((item, index) => ({
      ...item,
      rank: rank_offset + index + 1,
    }))
  }, [data, sortConfig, rank_offset])

  const sortedKeys = [...dynamicKeys].sort((a, b) => {
    if (table_type === 'month') return parseInt(a) - parseInt(b)
    if (table_type === 'year') return parseInt(a) - parseInt(b)
    if (table_type === 'quarter') return a.localeCompare(b)
    return 0
  })

  const displayLabels = sortedKeys.map((key) => {
    if (table_type === 'month') {
      return dayjs(`2025-${key}-01`).format('MMM')
    }
    if (table_type === 'quarter') {
      return `Q${key}`
    }
    return key
  })

  return (
    <div className={classes.tableContainer}>
      <Table.ScrollContainer minWidth={500}>
        <Table
          classNames={{
            table: classes.performance_kpi_table,
            tr: classes.tr,
            th: classes.th,
            td: classes.td,
          }}
          verticalSpacing="xs"
          withRowBorders={false}
        >
          <Table.Thead>
            <Table.Tr className={classes.headerRow}>
              <Table.Th>#</Table.Th>
              <Table.Th className={classes.mobileOnly}></Table.Th>
              <Table.Th style={{ textAlign: 'left' }}>
                {ctx.subject_type === 'user' ? 'Player' : 'Team'}
              </Table.Th>
              <Table.Th
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('ytd')}
              >
                YTD{' '}
                {sortConfig.key === 'ytd' &&
                  (sortConfig.direction === 'asc' ? '↓' : '↑')}
              </Table.Th>
              {sortedKeys.map((key, idx) => (
                <Table.Th
                  key={key}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort(`month-${key}`)}
                >
                  {displayLabels[idx]}{' '}
                  {sortConfig.key === `month-${key}` &&
                    (sortConfig.direction === 'asc' ? '↓' : '↑')}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedData.map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td>
                  <Typography fs="xs" fw={600} lh="130" ta="center">
                    {row.rank}
                    <sup>{getRankSuffix(row.rank)}</sup>
                  </Typography>
                </Table.Td>
                <Table.Td className={classes.mobileOnly}>
                  <AspectRatio ratio={1 / 1}>
                    <Image
                      src={row.logo || circlePlaceholder}
                      alt={row.name}
                      className={classes.team_logo}
                      fit="scale-down"
                      bdrs={subject_type === 'team' ? '0' : '50%'}
                    />
                  </AspectRatio>
                </Table.Td>
                <Table.Td className={classes.td_team_name}>
                  <div className={classes.team_name}>
                    <AspectRatio ratio={1 / 1} className={classes.desktopOnly}>
                      <Image
                        src={row.logo || circlePlaceholder}
                        alt={row.name}
                        className={classes.team_logo}
                        fit="scale-down"
                        bdrs={subject_type === 'team' ? '0' : '50%'}
                      />
                    </AspectRatio>
                    <Text className={classes.text} truncate="end">
                      {row.name}
                    </Text>
                  </div>
                </Table.Td>
                <Table.Td>
                  <FmtColored
                    value={row.ytd}
                    metric={metric}
                    component={component}
                    statistic={statistic}
                  />
                </Table.Td>
                {sortedKeys.map((key) => (
                  <Table.Td key={key}>
                    <FmtColored
                      value={row.values[key]}
                      metric={metric}
                      component={component}
                      statistic={statistic}
                      override={{ percent_decimal_places: 1 }}
                    />
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  )
}
